import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNowStrict } from "date-fns";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import { useServerFn } from "@/lib/server-fn-compat";
import {
  runHealthChecks,
  sendTestHealthAlert,
  type ApiMetricPoint,
  type CheckResult,
  type CheckStatus,
  type DbMetricPoint,
  type HealthCheckSummary,
  type HealthMetrics,
  type HealthMetricsRange,
  type HealthReport,
  type HealthRun,
  type HealthRunsDays,
  type LambdaMetricRow,
} from "@/lib/health.functions";
import {
  HEALTH_METRIC_RANGES,
  HEALTH_RUNS_KEY,
  HEALTH_RUN_WINDOWS,
  healthAlertSettingsQueryOptions,
  healthMetricsQueryOptions,
  healthRunsQueryOptions,
} from "@/lib/health.queries";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip as HoverTip,
  TooltipContent as HoverTipContent,
  TooltipProvider as HoverTipProvider,
  TooltipTrigger as HoverTipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  ExternalLink,
  FileClock,
  Loader2,
  RefreshCw,
  Send,
  Table2,
  XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/health")({
  component: HealthPage,
});

// ---------------------------------------------------------------------------
// Status palette (reserved, never themed; always paired with an icon + label).
// Requests, p50, 4xx and every single-series database chart share categorical
// slot 1 (blue); p99 and 5xx are slot 2 (orange). Same slots as the Traffic
// page, validated with the dataviz palette script in both modes.
// ---------------------------------------------------------------------------
const SLOT_1 = { light: "#2a78d6", dark: "#3987e5" };
const SLOT_2 = { light: "#eb6834", dark: "#d95926" };
const SLOT_1_BG = "bg-[#2a78d6] dark:bg-[#3987e5]";
const SLOT_2_BG = "bg-[#eb6834] dark:bg-[#d95926]";

const chartConfig = {
  requests: { label: "Requests", theme: SLOT_1 },
  err4xx: { label: "4xx", theme: SLOT_1 },
  err5xx: { label: "5xx", theme: SLOT_2 },
  latencyP50: { label: "p50", theme: SLOT_1 },
  latencyP99: { label: "p99", theme: SLOT_2 },
  capacity: { label: "Capacity (ACU)", theme: SLOT_1 },
  connections: { label: "Connections", theme: SLOT_1 },
  cpu: { label: "CPU", theme: SLOT_1 },
} satisfies ChartConfig;

const STATUS: Record<
  CheckStatus,
  { label: string; icon: typeof CheckCircle2; badge: string; row: string; swatch: string }
> = {
  pass: {
    label: "OK",
    icon: CheckCircle2,
    badge: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
    row: "",
    swatch: "bg-[#0ca30c]",
  },
  warn: {
    label: "Warning",
    icon: AlertTriangle,
    badge: "bg-amber-100 text-amber-900 hover:bg-amber-100",
    row: "bg-amber-50/60",
    swatch: "bg-[#fab219]",
  },
  fail: {
    label: "Failing",
    icon: XCircle,
    badge: "bg-red-100 text-red-800 hover:bg-red-100",
    row: "bg-red-50/70",
    swatch: "bg-[#d03b3b]",
  },
};

const runStatus = (r: HealthRun): CheckStatus =>
  r.fail > 0 ? "fail" : r.warn > 0 ? "warn" : "pass";

// ---------------------------------------------------------------------------
// Check groups
// ---------------------------------------------------------------------------
type GroupName = "Core" | "Delivery" | "Integrations" | "Other";

const GROUPS: Array<{ name: GroupName; description: string; ids: string[] }> = [
  {
    name: "Core",
    description: "The database, the content it serves, sign-in and the background queues.",
    ids: ["database", "content", "intake", "cognito", "prerender-queue"],
  },
  {
    name: "Delivery",
    description: "How the public site reaches visitors: pages, media, certificate, DNS and logs.",
    ids: ["public-pages", "media-cdn", "tls-cert", "apex-redirect", "dns", "access-logs"],
  },
  {
    name: "Integrations",
    description: "Outbound email and the Shopify store.",
    ids: ["email-identity", "store", "shopify-admin"],
  },
];

function groupOf(id: string): GroupName {
  return GROUPS.find((g) => g.ids.includes(id))?.name ?? "Other";
}

const GROUP_ORDER: GroupName[] = ["Core", "Delivery", "Integrations", "Other"];

function groupChecks<T extends { id: string }>(items: T[]) {
  const byGroup = new Map<GroupName, T[]>();
  for (const it of items) {
    const g = groupOf(it.id);
    const list = byGroup.get(g) ?? [];
    list.push(it);
    byGroup.set(g, list);
  }
  return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
    name: g,
    description: GROUPS.find((x) => x.name === g)?.description ?? "Checks not yet grouped.",
    items: byGroup.get(g)!,
  }));
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
const fullFmt = new Intl.NumberFormat(undefined);
const compactFmt = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});
const fmtInt = (n: number) => fullFmt.format(Math.round(n));
const fmtCompact = (n: number) => compactFmt.format(n);

const fmtDay = (iso: string) => format(new Date(iso), "MMM d");
const fmtDayTime = (iso: string) => format(new Date(iso), "MMM d, HH:mm");
const fmtLongDateTime = (iso: string) => format(new Date(iso), "MMM d, yyyy HH:mm");
const fmtLongDate = (iso: string) => format(new Date(iso), "MMM d, yyyy");

const validDate = (iso: string | null | undefined) => !!iso && !Number.isNaN(Date.parse(iso));

function fmtAgo(iso: string) {
  if (!validDate(iso)) return "unknown time";
  return `${formatDistanceToNowStrict(new Date(iso))} ago`;
}

function fmtDuration(ms: number | undefined) {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "-";
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms)} ms`;
}

function fmtLatency(ms: number) {
  if (!Number.isFinite(ms)) return "-";
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${Math.round(ms)} ms`;
}

const fmtAcu = (v: number) => (Number.isFinite(v) ? `${v.toFixed(1)} ACU` : "-");
const fmtPct = (v: number) => (Number.isFinite(v) ? `${v.toFixed(1)}%` : "-");

const RANGE_LABEL: Record<HealthMetricsRange, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
};

const ALERT_MODE_LABEL: Record<string, string> = {
  failures: "Only when a check fails",
  "on-failure": "Only when a check fails",
  always: "After every scheduled run",
  daily: "After every scheduled run",
  off: "Off",
  none: "Off",
};

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------
function StatusPill({ status }: { status: CheckStatus }) {
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <Badge className={cn("gap-1 rounded-full", s.badge)}>
      <Icon className="h-3 w-3" />
      {s.label}
    </Badge>
  );
}

/** Legend entry: a short 2px stroke in the series color, name in text ink. */
function LineKey({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span aria-hidden className={cn("inline-block h-0.5 w-4 rounded-full", className)} />
      {label}
    </span>
  );
}

function EmptyNote({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof FileClock;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center">
      <Icon className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm font-medium">{title}</p>
      {children && <p className="max-w-md text-xs text-muted-foreground">{children}</p>}
    </div>
  );
}

function ErrorNote({ label, error, onRetry }: { label: string; error: unknown; onRetry: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm text-red-700">
      <span>
        {label}: {error instanceof Error ? error.message : "unknown error"}
      </span>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-1 h-4 w-4" /> Try again
      </Button>
    </div>
  );
}

function LoadingNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
      <Loader2 className="h-4 w-4 animate-spin" />
      {children}
    </p>
  );
}

type TipPayloadItem = {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
  payload?: Record<string, unknown>;
};

/** One tooltip, every series at that X. Values lead, the series name follows. */
function SeriesTooltip({
  active,
  payload,
  title,
  formatValue,
}: Pick<TooltipProps<number, string>, "active" | "payload"> & {
  title: (row: Record<string, unknown>) => string;
  formatValue: (key: string, value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const items = payload as TipPayloadItem[];
  const row = (items[0]?.payload ?? {}) as Record<string, unknown>;
  return (
    <div className="min-w-[9rem] rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="mb-1 font-medium text-foreground">{title(row)}</div>
      <div className="grid gap-1">
        {items.map((it) => {
          const key = String(it.dataKey ?? it.name ?? "");
          const label = chartConfig[key as keyof typeof chartConfig]?.label ?? key;
          const v = typeof it.value === "number" ? it.value : Number(it.value ?? 0);
          return (
            <div key={key} className="flex items-center gap-2">
              <span
                aria-hidden
                className="inline-block h-0.5 w-3 shrink-0 rounded-full"
                style={{ background: it.color ?? `var(--color-${key})` }}
              />
              <span className="font-semibold tabular-nums text-foreground">
                {formatValue(key, v)}
              </span>
              <span className="text-muted-foreground">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

function useBucketLabels(series: Array<{ t: string }>) {
  return useMemo(() => {
    const subDay = series.length >= 2 && Date.parse(series[1].t) - Date.parse(series[0].t) < DAY_MS;
    const spansDays =
      series.length >= 2 &&
      Date.parse(series[series.length - 1].t) - Date.parse(series[0].t) > DAY_MS;
    const tick = (iso: string) => {
      const d = new Date(iso);
      if (!subDay) return format(d, "MMM d");
      return spansDays ? format(d, "MMM d HH:mm") : format(d, "HH:mm");
    };
    const full = (iso: string) => (subDay ? fmtDayTime(iso) : fmtDay(iso));
    return { tick, full };
  }, [series]);
}

const AXIS_PROPS = { tickLine: false, axisLine: false, tickMargin: 8 } as const;
const ACTIVE_DOT = { r: 4, strokeWidth: 2, stroke: "var(--color-background)" } as const;
const CURSOR_LINE = { stroke: "var(--color-border)", strokeWidth: 1 } as const;
const LINE_PROPS = {
  type: "monotone",
  strokeWidth: 2,
  strokeLinejoin: "round",
  strokeLinecap: "round",
  dot: false,
  activeDot: ACTIVE_DOT,
  isAnimationActive: false,
} as const;

const NUM_CELL = "py-1.5 text-right text-xs tabular-nums";

// ---------------------------------------------------------------------------
// 1. Checks card
// ---------------------------------------------------------------------------
type CheckRowData = {
  id: string;
  label: string;
  status: CheckStatus | null;
  detail: string;
  impact: string;
  ms?: number;
  summary?: HealthCheckSummary;
};

function HistoryNote({ summary }: { summary?: HealthCheckSummary }) {
  if (!summary) return <span className="text-xs text-muted-foreground">No history yet</span>;
  const failedOn = validDate(summary.lastFailAt) ? fmtLongDate(summary.lastFailAt!) : null;
  if (summary.failCount === 0 || !failedOn) {
    return <span className="text-xs text-muted-foreground">No failures in 30 days</span>;
  }
  return (
    <span className="text-xs text-muted-foreground">
      Last failed {failedOn} · failed {summary.failCount}{" "}
      {summary.failCount === 1 ? "time" : "times"} in 30 days
    </span>
  );
}

function CheckRow({ row }: { row: CheckRowData }) {
  const s = row.status ? STATUS[row.status] : null;
  return (
    <div
      className={cn(
        "grid gap-x-3 gap-y-1 border-b px-4 py-3 sm:grid-cols-[6.5rem_1.3fr_1.6fr_auto] sm:items-start",
        s?.row,
      )}
    >
      <div className="sm:pt-0.5">
        {row.status ? (
          <StatusPill status={row.status} />
        ) : (
          <Badge variant="outline" className="rounded-full text-muted-foreground">
            Not run
          </Badge>
        )}
      </div>
      <div>
        <div className="font-medium">{row.label}</div>
        {/* Only worth surfacing when it is actually broken. */}
        {row.status && row.status !== "pass" && row.impact && (
          <p className="mt-0.5 text-xs text-muted-foreground">{row.impact}</p>
        )}
      </div>
      <div className="text-sm text-muted-foreground">{row.detail}</div>
      <div className="flex flex-col items-start gap-0.5 sm:items-end sm:text-right">
        <HistoryNote summary={row.summary} />
        {typeof row.ms === "number" && (
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            {fmtDuration(row.ms)}
          </span>
        )}
      </div>
    </div>
  );
}

function ChecksCard({
  report,
  lastRun,
  byCheck,
  runsLoading,
  runsError,
  onRun,
  running,
  runError,
}: {
  report: HealthReport | undefined;
  lastRun: HealthRun | undefined;
  byCheck: HealthCheckSummary[];
  runsLoading: boolean;
  runsError: unknown;
  onRun: () => void;
  running: boolean;
  runError: unknown;
}) {
  const summaries = useMemo(() => new Map(byCheck.map((c) => [c.id, c])), [byCheck]);

  // Fresh report wins; otherwise fall back to the last recorded status per check.
  const rows: CheckRowData[] = useMemo(() => {
    if (report) {
      return report.checks.map((c) => ({ ...c, summary: summaries.get(c.id) }));
    }
    return byCheck.map((c) => ({
      id: c.id,
      label: c.label,
      status: c.lastStatus,
      detail: validDate(c.lastRunAt)
        ? `Last checked ${fmtAgo(c.lastRunAt!)}. Run checks for details.`
        : "Run checks for details.",
      impact: "",
      summary: c,
    }));
  }, [report, byCheck, summaries]);

  const groups = useMemo(() => groupChecks(rows), [rows]);

  // Banner state: from this session's run, else from the newest recorded run.
  const banner = report
    ? { ok: report.ok, counts: report.counts, ranAt: report.ranAt, durationMs: report.durationMs, trigger: "manual" as const }
    : lastRun
      ? {
          ok: lastRun.ok,
          counts: { pass: lastRun.pass, warn: lastRun.warn, fail: lastRun.fail },
          ranAt: lastRun.ranAt,
          durationMs: lastRun.durationMs,
          trigger: lastRun.trigger,
        }
      : null;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>System Health</CardTitle>
          <CardDescription>
            End-to-end checks against the live site: database, content, form intake, sign-in,
            outbound email, the store, media delivery, certificate, DNS and the public pages. The
            same checks run automatically every day at 13:00 UTC.
          </CardDescription>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <Button onClick={onRun} disabled={running}>
            <RefreshCw className={cn("mr-1 h-4 w-4", running && "animate-spin")} />
            {running ? "Running…" : "Run checks"}
          </Button>
          <span className="text-xs text-muted-foreground">
            {runsLoading
              ? "Loading history…"
              : lastRun && validDate(lastRun.ranAt)
                ? `Last run ${fmtAgo(lastRun.ranAt)} (${lastRun.trigger})`
                : "No runs recorded yet"}
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {runError != null && (
          <p className="border-b p-4 text-sm text-red-700">
            Could not run the checks: {runError instanceof Error ? runError.message : "unknown error"}
          </p>
        )}
        {runsError != null && (
          <p className="border-b p-4 text-sm text-red-700">
            Could not load run history:{" "}
            {runsError instanceof Error ? runsError.message : "unknown error"}
          </p>
        )}

        {banner && (
          <div
            className={cn(
              "flex flex-wrap items-center gap-x-6 gap-y-1 border-b px-4 py-3 text-sm",
              banner.ok ? "bg-emerald-50/70" : "bg-red-50/70",
            )}
          >
            <span className="flex items-center gap-1.5 font-semibold">
              {banner.ok ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              {banner.ok ? "All systems normal" : `${banner.counts.fail} check(s) failing`}
            </span>
            <span className="text-muted-foreground">
              {banner.counts.pass} OK · {banner.counts.warn} warning · {banner.counts.fail} failing
            </span>
            <span className="text-muted-foreground">
              {validDate(banner.ranAt) ? fmtLongDateTime(banner.ranAt!) : "time unknown"}
              {" · "}
              {banner.trigger}
              {typeof banner.durationMs === "number" && ` · ${fmtDuration(banner.durationMs)}`}
            </span>
            {!report && (
              <span className="text-xs text-muted-foreground">
                From the last recorded run. Press Run checks for a live result.
              </span>
            )}
          </div>
        )}

        {rows.length === 0 && !running && (
          <p className="p-6 text-sm text-muted-foreground">
            {runsLoading ? (
              <LoadingNote>Loading the last recorded run…</LoadingNote>
            ) : (
              <>
                Press <span className="font-medium">Run checks</span> to test the live site. Takes
                a few seconds, longer if the database has gone to sleep.
              </>
            )}
          </p>
        )}

        {rows.length === 0 && running && (
          <div className="p-6">
            <LoadingNote>Running checks against the live site…</LoadingNote>
          </div>
        )}

        {groups.map((g) => (
          <section key={g.name} className={cn(running && "opacity-60 transition-opacity")}>
            <div className="flex flex-wrap items-baseline gap-x-3 border-b bg-muted/40 px-4 py-2">
              <h3 className="text-sm font-semibold">{g.name}</h3>
              <span className="text-xs text-muted-foreground">{g.description}</span>
            </div>
            {g.items.map((row) => (
              <CheckRow key={row.id} row={row} />
            ))}
          </section>
        ))}

        {rows.length > 0 && (
          <p className="p-4 text-xs text-muted-foreground">
            The intake check writes a probe row and rolls it back, so nothing appears in Messages.
            A warning means degraded but still serving, for example a sleeping database or a stale
            store cache. Failure counts cover the last 30 days.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 2. History card
// ---------------------------------------------------------------------------
function RunStrip({ runs }: { runs: HealthRun[] }) {
  // Oldest on the left, like a timeline.
  const ordered = useMemo(() => [...runs].reverse(), [runs]);
  return (
    <HoverTipProvider delayDuration={100}>
      <div className="space-y-2">
        <div
          className="flex items-stretch gap-0.5 overflow-x-auto rounded-md bg-muted/40 p-1"
          role="list"
          aria-label="Health runs, oldest to newest"
        >
          {ordered.map((r) => {
            const st = runStatus(r);
            const s = STATUS[st];
            const Icon = s.icon;
            return (
              <HoverTip key={r.id}>
                <HoverTipTrigger asChild>
                  <button
                    type="button"
                    role="listitem"
                    aria-label={`${fmtLongDateTime(r.ranAt)}, ${r.trigger}, ${s.label}${
                      r.failing.length ? `: ${r.failing.join(", ")}` : ""
                    }`}
                    className="group flex min-w-[10px] max-w-[22px] flex-1 items-end justify-center py-0.5"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "block h-7 w-full rounded-[3px] transition-transform group-hover:scale-y-110 group-focus-visible:ring-2 group-focus-visible:ring-ring",
                        s.swatch,
                      )}
                    />
                  </button>
                </HoverTipTrigger>
                <HoverTipContent side="top" className="max-w-xs">
                  <div className="flex items-center gap-1.5 font-medium">
                    <Icon className="h-3.5 w-3.5" />
                    {s.label} · {validDate(r.ranAt) ? fmtLongDateTime(r.ranAt) : "time unknown"}
                  </div>
                  <div className="opacity-80">
                    {r.trigger} · {r.pass} OK · {r.warn} warning · {r.fail} failing ·{" "}
                    {fmtDuration(r.durationMs)}
                  </div>
                  {r.failing.length > 0 && (
                    <div className="mt-1 font-mono text-[11px]">{r.failing.join(", ")}</div>
                  )}
                </HoverTipContent>
              </HoverTip>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {(["pass", "warn", "fail"] as CheckStatus[]).map((st) => {
            const s = STATUS[st];
            const Icon = s.icon;
            return (
              <span key={st} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <span aria-hidden className={cn("inline-block h-2.5 w-2.5 rounded-[2px]", s.swatch)} />
                <Icon className="h-3 w-3" />
                {s.label}
              </span>
            );
          })}
          <span className="text-xs text-muted-foreground">
            {runs.length} {runs.length === 1 ? "run" : "runs"}, oldest to newest. Hover a bar for
            the failing checks.
          </span>
        </div>
      </div>
    </HoverTipProvider>
  );
}

function RunsTable({ runs, labels }: { runs: HealthRun[]; labels: Map<string, string> }) {
  return (
    <div className="max-h-96 overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead>Result</TableHead>
            <TableHead className="text-right">OK</TableHead>
            <TableHead className="text-right">Warning</TableHead>
            <TableHead className="text-right">Failing</TableHead>
            <TableHead className="text-right">Duration</TableHead>
            <TableHead>Failing checks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="whitespace-nowrap py-1.5 text-xs">
                {validDate(r.ranAt) ? fmtLongDateTime(r.ranAt) : "-"}
              </TableCell>
              <TableCell className="py-1.5 text-xs capitalize">{r.trigger}</TableCell>
              <TableCell className="py-1.5">
                <StatusPill status={runStatus(r)} />
              </TableCell>
              <TableCell className={NUM_CELL}>{r.pass}</TableCell>
              <TableCell className={NUM_CELL}>{r.warn}</TableCell>
              <TableCell className={NUM_CELL}>{r.fail}</TableCell>
              <TableCell className={NUM_CELL}>{fmtDuration(r.durationMs)}</TableCell>
              <TableCell className="py-1.5 text-xs">
                {r.failing.length === 0 ? (
                  <span className="text-muted-foreground">-</span>
                ) : (
                  r.failing.map((id) => labels.get(id) ?? id).join(", ")
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function HistoryCard({ days, onDaysChange }: { days: HealthRunsDays; onDaysChange: (d: HealthRunsDays) => void }) {
  const q = useQuery(healthRunsQueryOptions(days));
  const data = q.data;
  const dimmed = q.isPlaceholderData || (q.isFetching && !!data);
  const labels = useMemo(
    () => new Map((data?.byCheck ?? []).map((c) => [c.id, c.label])),
    [data],
  );

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="text-base">History</CardTitle>
          <CardDescription>
            Every recorded run, scheduled and manual, for the last {days} days.
          </CardDescription>
        </div>
        <Tabs value={String(days)} onValueChange={(v) => onDaysChange(Number(v) as HealthRunsDays)}>
          <TabsList aria-label="History window">
            {HEALTH_RUN_WINDOWS.map((d) => (
              <TabsTrigger key={d} value={String(d)}>
                {d} days
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent className="space-y-4">
        {q.isPending && <LoadingNote>Loading run history…</LoadingNote>}
        {q.isError && (
          <ErrorNote label="Could not load run history" error={q.error} onRetry={() => q.refetch()} />
        )}
        {data && data.runs.length === 0 && (
          <EmptyNote icon={FileClock} title="No history yet">
            Runs are recorded from the next scheduled check or the next time someone presses Run
            checks.
          </EmptyNote>
        )}
        {data && data.runs.length > 0 && (
          <div className={cn("space-y-4 transition-opacity", dimmed && "opacity-60")}>
            <RunStrip runs={data.runs} />
            <RunsTable runs={data.runs} labels={labels} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 3. Platform metrics card
// ---------------------------------------------------------------------------
function RequestsChart({ series }: { series: ApiMetricPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-52 w-full">
      <AreaChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#ccc" />
        <XAxis dataKey="t" {...AXIS_PROPS} minTickGap={40} tickFormatter={labels.tick} />
        <YAxis {...AXIS_PROPS} width={44} allowDecimals={false} tickFormatter={fmtCompact} />
        <Tooltip
          cursor={CURSOR_LINE}
          isAnimationActive={false}
          content={
            <SeriesTooltip title={(row) => labels.full(String(row.t))} formatValue={(_k, v) => fmtInt(v)} />
          }
        />
        <Area
          {...LINE_PROPS}
          dataKey="requests"
          stroke="var(--color-requests)"
          fill="var(--color-requests)"
          fillOpacity={0.1}
        />
      </AreaChart>
    </ChartContainer>
  );
}

function ErrorsChart({ series }: { series: ApiMetricPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-40 w-full">
      <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#ccc" />
        <XAxis dataKey="t" {...AXIS_PROPS} minTickGap={40} tickFormatter={labels.tick} />
        <YAxis {...AXIS_PROPS} width={44} allowDecimals={false} tickFormatter={fmtCompact} />
        <Tooltip
          cursor={CURSOR_LINE}
          isAnimationActive={false}
          content={
            <SeriesTooltip title={(row) => labels.full(String(row.t))} formatValue={(_k, v) => fmtInt(v)} />
          }
        />
        <Line {...LINE_PROPS} dataKey="err4xx" stroke="var(--color-err4xx)" />
        <Line {...LINE_PROPS} dataKey="err5xx" stroke="var(--color-err5xx)" />
      </LineChart>
    </ChartContainer>
  );
}

function LatencyChart({ series }: { series: ApiMetricPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-40 w-full">
      <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#ccc" />
        <XAxis dataKey="t" {...AXIS_PROPS} minTickGap={40} tickFormatter={labels.tick} />
        <YAxis {...AXIS_PROPS} width={52} tickFormatter={(v: number) => fmtLatency(v)} />
        <Tooltip
          cursor={CURSOR_LINE}
          isAnimationActive={false}
          content={
            <SeriesTooltip
              title={(row) => labels.full(String(row.t))}
              formatValue={(_k, v) => fmtLatency(v)}
            />
          }
        />
        <Line {...LINE_PROPS} dataKey="latencyP50" stroke="var(--color-latencyP50)" />
        <Line {...LINE_PROPS} dataKey="latencyP99" stroke="var(--color-latencyP99)" />
      </LineChart>
    </ChartContainer>
  );
}

/** One small multiple per database measure: three units, three axes, never a dual axis. */
function DbChart({
  series,
  dataKey,
  formatValue,
  domain,
}: {
  series: DbMetricPoint[];
  dataKey: "capacity" | "connections" | "cpu";
  formatValue: (v: number) => string;
  domain?: [number, number];
}) {
  const labels = useBucketLabels(series);
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-36 w-full">
      <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke="#ccc" />
        <XAxis dataKey="t" {...AXIS_PROPS} minTickGap={40} tickFormatter={labels.tick} />
        <YAxis
          {...AXIS_PROPS}
          width={48}
          domain={domain}
          allowDecimals={dataKey !== "connections"}
          tickFormatter={(v: number) => (dataKey === "cpu" ? `${v}%` : dataKey === "capacity" ? v.toFixed(1) : fmtCompact(v))}
        />
        <Tooltip
          cursor={CURSOR_LINE}
          isAnimationActive={false}
          content={
            <SeriesTooltip
              title={(row) => labels.full(String(row.t))}
              formatValue={(_k, v) => formatValue(v)}
            />
          }
        />
        <Line {...LINE_PROPS} dataKey={dataKey} stroke={`var(--color-${dataKey})`} />
      </LineChart>
    </ChartContainer>
  );
}

/** Table twin of the API charts, so every plotted value is readable without hovering. */
function ApiTable({ series }: { series: ApiMetricPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <div className="max-h-72 overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bucket start</TableHead>
            <TableHead className="text-right">Requests</TableHead>
            <TableHead className="text-right">4xx</TableHead>
            <TableHead className="text-right">5xx</TableHead>
            <TableHead className="text-right">p50</TableHead>
            <TableHead className="text-right">p99</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {series.map((p) => (
            <TableRow key={p.t}>
              <TableCell className="py-1.5 text-xs">{labels.full(p.t)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtInt(p.requests)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtInt(p.err4xx)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtInt(p.err5xx)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtLatency(p.latencyP50)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtLatency(p.latencyP99)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DbTable({ series }: { series: DbMetricPoint[] }) {
  const labels = useBucketLabels(series);
  return (
    <div className="max-h-72 overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bucket start</TableHead>
            <TableHead className="text-right">Capacity</TableHead>
            <TableHead className="text-right">Connections</TableHead>
            <TableHead className="text-right">CPU</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {series.map((p) => (
            <TableRow key={p.t}>
              <TableCell className="py-1.5 text-xs">{labels.full(p.t)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtAcu(p.capacity)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtInt(p.connections)}</TableCell>
              <TableCell className={NUM_CELL}>{fmtPct(p.cpu)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LambdaTable({ rows }: { rows: LambdaMetricRow[] }) {
  const sorted = useMemo(
    () =>
      [...rows].sort(
        (a, b) => b.errors - a.errors || b.throttles - a.throttles || b.invocations - a.invocations,
      ),
    [rows],
  );
  return (
    <div className="max-h-80 overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Function</TableHead>
            <TableHead className="text-right">Invocations</TableHead>
            <TableHead className="text-right">Errors</TableHead>
            <TableHead className="text-right">Error rate</TableHead>
            <TableHead className="text-right">Throttles</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((r) => {
            const rate = r.invocations > 0 ? (r.errors / r.invocations) * 100 : 0;
            const bad = r.errors > 0 || r.throttles > 0;
            return (
              <TableRow key={r.name} className={cn(bad && "bg-amber-50/60")}>
                <TableCell className="py-1.5 font-mono text-xs">{r.name}</TableCell>
                <TableCell className={NUM_CELL}>{fmtInt(r.invocations)}</TableCell>
                <TableCell className={cn(NUM_CELL, r.errors > 0 && "font-semibold")}>
                  {fmtInt(r.errors)}
                </TableCell>
                <TableCell className={NUM_CELL}>
                  {r.invocations > 0 ? (rate < 0.05 && rate > 0 ? "<0.1%" : `${rate.toFixed(1)}%`) : "-"}
                </TableCell>
                <TableCell className={cn(NUM_CELL, r.throttles > 0 && "font-semibold")}>
                  {fmtInt(r.throttles)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function MetricsBody({ data, dimmed }: { data: HealthMetrics; dimmed: boolean }) {
  const [showApiTable, setShowApiTable] = useState(false);
  const [showDbTable, setShowDbTable] = useState(false);
  const hasApi = data.api.length > 0;
  const hasDb = data.db.length > 0;
  const hasLambdas = data.lambdas.length > 0;

  return (
    <div className={cn("space-y-6 transition-opacity", dimmed && "opacity-60")}>
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">API</h3>
            <p className="text-xs text-muted-foreground">
              {hasApi
                ? `${fmtDayTime(data.from)} to ${fmtDayTime(data.to)} · ${data.api.length} buckets · API Gateway metrics from CloudWatch`
                : "API Gateway metrics from CloudWatch."}
            </p>
          </div>
          {hasApi && (
            <Button variant="ghost" size="sm" onClick={() => setShowApiTable((v) => !v)}>
              <Table2 className="mr-1 h-4 w-4" />
              {showApiTable ? "Hide table" : "Show table"}
            </Button>
          )}
        </div>
        {!hasApi ? (
          <EmptyNote icon={FileClock} title="No API metrics in this range">
            CloudWatch has no API Gateway data points for the selected period.
          </EmptyNote>
        ) : (
          <>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-sm font-medium">Requests</span>
              </div>
              <RequestsChart series={data.api} />
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-sm font-medium">Errors</span>
                <LineKey className={SLOT_1_BG} label="4xx (client)" />
                <LineKey className={SLOT_2_BG} label="5xx (server)" />
              </div>
              <ErrorsChart series={data.api} />
            </div>
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-sm font-medium">Latency</span>
                <LineKey className={SLOT_1_BG} label="p50 (typical)" />
                <LineKey className={SLOT_2_BG} label="p99 (slowest 1%)" />
              </div>
              <LatencyChart series={data.api} />
            </div>
            {showApiTable && <ApiTable series={data.api} />}
          </>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Database</h3>
            <p className="text-xs text-muted-foreground">
              Aurora Serverless capacity, open connections and CPU. Each measure has its own scale.
            </p>
          </div>
          {hasDb && (
            <Button variant="ghost" size="sm" onClick={() => setShowDbTable((v) => !v)}>
              <Table2 className="mr-1 h-4 w-4" />
              {showDbTable ? "Hide table" : "Show table"}
            </Button>
          )}
        </div>
        {!hasDb ? (
          <EmptyNote icon={FileClock} title="No database metrics in this range">
            CloudWatch has no RDS data points for the selected period. A paused cluster reports
            nothing until it wakes.
          </EmptyNote>
        ) : (
          <>
            <div className="grid gap-4 lg:grid-cols-3">
              <div>
                <span className="text-sm font-medium">Capacity (ACU)</span>
                <DbChart series={data.db} dataKey="capacity" formatValue={fmtAcu} />
              </div>
              <div>
                <span className="text-sm font-medium">Connections</span>
                <DbChart series={data.db} dataKey="connections" formatValue={fmtInt} />
              </div>
              <div>
                <span className="text-sm font-medium">CPU</span>
                <DbChart series={data.db} dataKey="cpu" formatValue={fmtPct} domain={[0, 100]} />
              </div>
            </div>
            {showDbTable && <DbTable series={data.db} />}
          </>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold">Lambda functions</h3>
          <p className="text-xs text-muted-foreground">
            Invocations, errors and throttles per function over the selected range, worst first.
          </p>
        </div>
        {!hasLambdas ? (
          <EmptyNote icon={FileClock} title="No Lambda metrics in this range">
            No function reported invocations in the selected period.
          </EmptyNote>
        ) : (
          <LambdaTable rows={data.lambdas} />
        )}
      </section>
    </div>
  );
}

function MetricsCard() {
  const [range, setRange] = useState<HealthMetricsRange>("24h");
  const q = useQuery(healthMetricsQueryOptions(range));
  const dimmed = q.isPlaceholderData || (q.isFetching && !!q.data);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="text-base">Platform metrics</CardTitle>
          <CardDescription>
            What the API, the database and the Lambda functions have been doing over the last{" "}
            {RANGE_LABEL[range]}. The range applies to everything in this card.
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={range} onValueChange={(v) => setRange(v as HealthMetricsRange)}>
            <TabsList aria-label="Metrics range">
              {HEALTH_METRIC_RANGES.map((r) => (
                <TabsTrigger key={r} value={r}>
                  {r}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={cn("mr-1 h-4 w-4", q.isFetching && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {q.isPending && <LoadingNote>Loading metrics for the last {RANGE_LABEL[range]}…</LoadingNote>}
        {q.isError && (
          <ErrorNote label="Could not load metrics" error={q.error} onRetry={() => q.refetch()} />
        )}
        {q.data && <MetricsBody data={q.data} dimmed={dimmed} />}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 4. Alerts card
// ---------------------------------------------------------------------------
function AlertsCard() {
  const { isAdmin } = useAuth();
  const q = useQuery(healthAlertSettingsQueryOptions());
  const send = useServerFn(sendTestHealthAlert);
  const test = useMutation({
    mutationFn: () => send(),
    onSuccess: (res) => {
      const to = Array.isArray(res.to) ? res.to.join(", ") : res.to;
      toast.success(to ? `Test alert sent to ${to}` : "Test alert sent");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not send the test alert"),
  });

  const settings = q.data;
  const modeLabel = settings?.mode
    ? (ALERT_MODE_LABEL[settings.mode.toLowerCase()] ?? settings.mode)
    : "Not set";
  const noRecipients = !!settings && settings.recipients.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle className="text-base">Alerts</CardTitle>
          <CardDescription>
            Who hears about a failing check, and when. Edit these in{" "}
            <Link to="/admin/settings" className="inline-flex items-center gap-1 underline underline-offset-2">
              Settings &gt; Notifications
              <ExternalLink className="h-3 w-3" />
            </Link>
            .
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => test.mutate()}
          disabled={!isAdmin || test.isPending || q.isPending || noRecipients}
          title={
            !isAdmin
              ? "Only admins can send a test alert"
              : noRecipients
                ? "Add a recipient in Settings first"
                : undefined
          }
        >
          {test.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-1 h-4 w-4" />
          )}
          {test.isPending ? "Sending…" : "Send test alert"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isPending && <LoadingNote>Loading alert settings…</LoadingNote>}
        {q.isError && (
          <ErrorNote label="Could not load alert settings" error={q.error} onRetry={() => q.refetch()} />
        )}
        {settings && (
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[8rem_1fr]">
            <dt className="text-muted-foreground">Recipients</dt>
            <dd>
              {settings.recipients.length === 0 ? (
                <span className="inline-flex items-center gap-1.5 text-amber-900">
                  <AlertTriangle className="h-4 w-4" />
                  No recipients. Failing checks will not be emailed to anyone.
                </span>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {settings.recipients.map((r) => (
                    <Badge key={r} variant="outline" className="rounded-full font-normal">
                      {r}
                    </Badge>
                  ))}
                </div>
              )}
            </dd>
            <dt className="text-muted-foreground">Mode</dt>
            <dd className="inline-flex items-center gap-1.5">
              <BellRing className="h-4 w-4 text-muted-foreground" />
              {modeLabel}
            </dd>
          </dl>
        )}
        <p className="text-xs text-muted-foreground">
          A test alert uses the real delivery path, so it confirms both the recipient list and the
          sending domain. Only admins can send one.
        </p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function HealthPage() {
  const qc = useQueryClient();
  const run = useServerFn(runHealthChecks);
  const [historyDays, setHistoryDays] = useState<HealthRunsDays>(7);

  // The checks card always reads the 30-day summary, whatever the history card shows.
  const runs30 = useQuery(healthRunsQueryOptions(30));

  const check = useMutation({
    mutationFn: () => run(),
    // A manual run is recorded server-side; pull the history so it shows up.
    onSettled: () => qc.invalidateQueries({ queryKey: HEALTH_RUNS_KEY }),
  });

  return (
    <div className="space-y-6">
      <ChecksCard
        report={check.data}
        lastRun={runs30.data?.runs[0]}
        byCheck={runs30.data?.byCheck ?? []}
        runsLoading={runs30.isPending}
        runsError={runs30.isError ? runs30.error : null}
        onRun={() => check.mutate()}
        running={check.isPending}
        runError={check.isError ? check.error : null}
      />
      <HistoryCard days={historyDays} onDaysChange={setHistoryDays} />
      <MetricsCard />
      <AlertsCard />
    </div>
  );
}
